import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Users } from "lucide-react";

interface CircleEventAttendeesRowProps {
  eventId: string;
}

interface AttendeeRow {
  bookingId: string;
  userId: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  nimiTickets: number;
  circleAttended: number;
  circleHosted: number;
}

export function CircleEventAttendeesRow({ eventId }: CircleEventAttendeesRowProps) {
  const { data: attendees, isLoading } = useQuery({
    queryKey: ["admin-circle-event-attendees", eventId],
    queryFn: async (): Promise<AttendeeRow[]> => {
      const { data: bookings, error } = await supabase
        .from("circle_bookings")
        .select(`
          id, user_id,
          user:profiles!circle_bookings_user_id_fkey(
            id, first_name, last_name, email, phone, avatar_url
          )
        `)
        .eq("circle_event_id", eventId)
        .eq("status", "confirmed");

      if (error) throw error;
      if (!bookings || bookings.length === 0) return [];

      // Build per-user history counts in parallel
      const rows = await Promise.all(
        bookings.map(async (b: any) => {
          const user = Array.isArray(b.user) ? b.user[0] : b.user;
          const uid = b.user_id;

          const [ticketsRes, circleAttendedRes, circleHostedRes] = await Promise.all([
            supabase
              .from("tickets")
              .select("*", { count: "exact", head: true })
              .eq("user_id", uid)
              .eq("status", "confirmed"),
            supabase
              .from("circle_bookings")
              .select("*", { count: "exact", head: true })
              .eq("user_id", uid)
              .eq("status", "confirmed"),
            supabase
              .from("circle_events")
              .select("*", { count: "exact", head: true })
              .eq("host_id", uid)
              .eq("status", "approved"),
          ]);

          return {
            bookingId: b.id,
            userId: uid,
            fullName: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "—",
            email: user?.email || null,
            phone: user?.phone || null,
            avatarUrl: user?.avatar_url || null,
            nimiTickets: ticketsRes.count || 0,
            circleAttended: circleAttendedRes.count || 0,
            circleHosted: circleHostedRes.count || 0,
          };
        })
      );

      return rows;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 bg-muted/30">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!attendees || attendees.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground bg-muted/30">
        <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
        No confirmed attendees yet
      </div>
    );
  }

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div className="bg-muted/30 p-4">
      <p className="text-sm font-medium mb-3 text-muted-foreground">
        {attendees.length} confirmed attendee{attendees.length !== 1 ? "s" : ""}
      </p>
      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead className="text-center">Booking History</TableHead>
              <TableHead className="text-center">Circle History</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {attendees.map((a) => (
              <TableRow key={a.bookingId}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={a.avatarUrl || undefined} />
                      <AvatarFallback className="text-xs">
                        {getInitials(a.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{a.fullName}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {a.email || "—"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {a.phone || "—"}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {a.nimiTickets} {a.nimiTickets === 1 ? "event" : "events"}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {a.circleAttended} attended / {a.circleHosted} hosted
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
